<template>
  <div
    v-if="ready"
    v-observe-visibility="{
				callback: onVisibilityChange,
				throttle: 50,
				intersection: getObserverOptions(true)
			}"
    :class="classes"
    :style="style"
    tabindex="0"
  >
    <component
      :is="blockComponent"
      ref="innerElement"
      :block="blockRef"
      :block-data="blockData"
      :blockId="blockKey"
      :is-visible="isVisible"
      :page="page"
      :seen="seen"
    />
    <template v-if="isXRayEnabled">
      <x-ray-click-regions
        :block-data="blockData"
        :block-rendered="renderedBlockData"
        :flyer-data="pages"
        :flyer-rendered="flyerRenderedData"
        :imageRendered="imageRenderedData"
        :page="page"
        :page-rendered="pageRenderedData"
        :pageData="page"
        :product-rendered="productRenderedData" />
    </template>
  </div>
</template>

<script>

import { mapGetters, mapState } from "vuex";
import ProductElement from "@/components/block/elements/ProductElement.vue";
import ProductCarousel from "@/components/block/elements/ProductCarousel.vue";
import ProductCarouselV2 from "@/components/block/elements/ProductCarouselV2.vue";
import AdElement from "@/components/block/elements/AdElement.vue";
//import ProductBlockVideoElement from "@/components/block/elements/ProductBlockVideoElement.vue";
import XRayClickRegions from "@/features/x-ray/XRayClickRegions.vue";
import XRayModal from "@/features/x-ray/XRayModal.vue";
import Lytics from "@/classes/Lytics";
import ProductBlockVideoElement from "@/components/block/elements/ProductBlockVideoElement.vue";

export default {
  name: "FlyerBlock",
  props: {
    page: {
      type: Object,
      default: () => {
        return {};
      }
    },
    blockData: {
      type: Object
    },
    rootScrollContainer: {
      type: String || Object
    },
    blockKey: {
      type: String
    }
  },
  components: { XRayModal, XRayClickRegions },
  data() {
    return {
      isVisible: false,
      isInViewPort: false,
      ready: false,
      seen: false,
      classes: {
        'flyer-block': true
      },
      inBlockVisibleState: false
    }
  },
  async mounted() {
    this.ready = true;
    this.classes[this.blockComponent.name] = true;

    await this.$nextTick();
    const options = this.getObserverOptions(false);
    const observer = new IntersectionObserver(this.onViewPortChange, options);

    const target = this.$refs?.innerElement?.$el;
    observer.observe(target);

/*    setTimeout(() => {
      this.$nextTick(() => {
        const options = this.getObserverOptions(false);
        const observer = new IntersectionObserver(this.onViewPortChange, options);

        const target = this.$refs.innerElement.$el;
        observer.observe(target);
      });
    }, 500);*/
  },
  methods: {
    getObserverOptions(marginIncluded) {
      const observerOptions = {
        root: document.getElementById(this.rootScrollContainer),
        rootMargin: marginIncluded ? "100%" : "0px",
      };
      return observerOptions;
    },

    // separate intersection observer callback because loading the block images happens before scrolling into viewport
    // vs. analytics impression happens when it is in viewport
    onViewPortChange(entries) {
      entries.forEach(entry => {
        this.isInViewPort = entry.isIntersecting;
      });
    },

    onVisibilityChange(isVisible) {
      this.isVisible = isVisible;
      if (isVisible && !this.seen) {
        this.seen = true;
      }
    }
  },
  computed: {
    ...mapState(['pages', 'pageRenderedData', 'flyerRenderedData', 'productRenderedData', 'imageRenderedData']),
    ...mapGetters('screen', ['displayMode', 'flyerOrientation']),
    ...mapGetters('config', ['features', 'branding']),
    ...mapGetters(['isXRayEnabled']),
    unitWidth() {
      return this.page.unitWidth;
    },
    unitHeight() {
      return this.page.unitHeight;
    },
    rowSpan() {
      return this.blockData.rowSpan;
    },
    colSpan() {
      return this.blockData.colSpan;
    },
    width() {
      return this.unitWidth * this.colSpan;
    },
    height() {
      return this.unitHeight * this.rowSpan;
    },
    isCarousel() {
      return (this.blockData?.carouselBlocks?.length ?? 0) >= 1;
    },
    isAdPage() {
      return this.page.isAdPage;
    },
    isSearchResult() {
      return this.page.name === "search_results";
    },
    hasInBlockVideo() {
      const products = this.blockData?.products || [];
      return products.some((prod) => {
        const isVideo = prod.actionType || prod.sku;
        return (isVideo)?.toLowerCase().includes("inblock");// || (isVideo)?.toLowerCase().includes("animate"));
      });
    },
    blockComponent() {
      if (this.blockData?.type === 'component') return this.blockData.component;
      if (this.isCarousel && this.features?.carousels?.template === 'v2') return ProductCarouselV2;
      if (this.isCarousel) return ProductCarousel;
      if (this.isAdPage) return AdElement;
      if (this.hasInBlockVideo) return ProductBlockVideoElement;
      return ProductElement;
    },
    placement() {
      let pm = this.blockData.placement[this.displayMode];
      if (this.displayMode === 'desktop' && this.flyerOrientation === 'vertical') {
        pm = this.blockData.placement['tablet'];
      }
      return pm;
    },
    hasPlacement() {
      return this.placement?.visible;
    },
    style() {
      const order = this.placement?.order || 'initial';
      let s = {
        gridColumn: `span ${this.blockData.colSpan}`,
        gridRow: `span ${this.blockData.rowSpan}`,
        order: order,
        display: 'grid',
        position:'relative'// `${this.canShowAd ? "block" : "none"}`,,
      };

      if (this.isSearchResult) {
        delete s.order;
      }

      if (this.isAdPage) {
        const adMode =  this?.branding?.ads?.orientation?.[this.flyerOrientation];
        if (adMode?.alignment === 'auto') {
          s.height = `inherit`;
          if(adMode?.offset){
            s.height = `calc(100% + ${adMode?.offset})`;
          }
        }
      }

      return s;
    },
    blockRef() {
      return this;
    },
    renderedBlockData() {
      return {
        unitWidth: this.unitWidth,
        unitHeight: this.unitHeight,
        rowSpan: this.rowSpan,
        colSpan: this.colSpan,
        width: this.width,
        height: this.height,
        placement: this.placement,
        style: this.style
      };
    }
  },
  watch: {
    isInViewPort(val) {
      if (val) {
        const videoUrl = this.blockData.products.find(p => p.sku === "Play")?.url;
        const inblockUrl = this.blockData.products.find(p => p.sku === 'Inblock' || p.actionType === 'Inblock')?.url
        const linkUrl = this.blockData.linkUrl;

        let item_list_name = 'flyer';
        if (videoUrl) {
          item_list_name = 'video impression';
          Lytics.TriggerVideoImpression({
            url: videoUrl,
            page_number: this.page.page_num
          });
        }
        if (this.isCarousel) {
          item_list_name = 'carousel impression';
        }
        if (linkUrl) {
          Lytics.TriggerLinkImpression({
            url: linkUrl,
            page_number: this.page.page_num,
          })
        }
        if (this.hasInBlockVideo) {
          item_list_name = 'in_block_video_impression';
          Lytics.TriggerInBlockVideoImpression({
            url: inblockUrl,
            page_number: this.page.page_num
          });
        }

        const products = this.blockData.products.filter(p => p.sku !== 'Play' && p.sku !== 'url');

        Lytics.addToEventQueue({
          item_list_name,
          page_number: this.page.page_num,
          items: products.map((p, i) => {
            return {
              product_id: p.sku,
              name: p.productEn || p.productFr,
              name_en: p.productEn,
              name_fr: p.productFr,
              price: p.regularPrice,
              position: i,
              sale_price: p.salePrice,
              category: p.mainCategoryEn || p.mainCategoryFr,
              category_2: p.subCategoryEn || p.subCategoryFr,
              image_url: p.productImage,
              quantity: 1,
            }
          })
        })

        if (this.isAdPage) {
          Lytics.TriggerAdImpression({
            name: this.page.name,
            url: this.blockData.linkUrl,
            image_url: this.blockData.images[0]?.image,
            size: `${this.page.pageWidth}x${this.page.pageHeight}`
          })
        }
      }
    }
  }
}
</script>
